import { ClassFilterType } from "@common/types/class";
import { FiltersTypes } from "@common/types/filters/general";
import { ALL_TIME_PRESET_ID, DATE_RANGE_PRESETS_BY_ID } from "@helpers/date";
import { AccountingReportFilters } from "../types";
import {
    AccountingReportBuiltInViewId,
    AccountingReportBuiltInViewSettings,
    getAccountingReportBuiltInViewSettings,
} from "./accountingReportBuiltInViews";
import {
    AccountingReportViewDefinition,
    AccountingReportViewId,
    getAccountingReportViewDefinition,
} from "./accountingReportViewRegistry";
import {
    localCalendarDayAsUTCMidnight,
    utcCalendarDayAsLocalMidnight,
} from "./reportViewDates";

type AccountingReportViewExtras = Pick<
    AccountingReportFilters,
    | "cycle"
    | "comparisons"
    | "comparisonDiffs"
    | "ratioTypes"
    | "classes"
    | "classFilterType"
    | "counterpartiesIds"
    | "withEmptyOrUnverifiedCounterparty"
    | "hasCounterparty"
    | "accountIds"
    | "subaccountIds"
    | "accountCodes"
    | "journalEntrySources"
    | "minAmount"
    | "maxAmount"
    | "direction"
    | "amountRounded"
    | "amountRoundingLevel"
    | "excludeFrom1099"
    | "search"
>;

export function buildAccountingReportViewExtras({
    reportId,
    filters,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
}): FiltersTypes {
    const definition = getAccountingReportViewDefinition(reportId, filters);
    return stripUndefined(buildExtras(definition, filters));
}

export function buildAccountingReportViewFilters({
    reportId,
    filters,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
}): FiltersTypes {
    const definition = getAccountingReportViewDefinition(reportId, filters);
    const extras = buildExtras(definition, filters);
    const context: FiltersTypes = {
        entityId: filters.entityId,
        entityIds: definition.hasGeneralLedgerFilters
            ? filters.entityIds
            : undefined,
        ledgerId: definition.hasLedger ? filters.ledgerId : undefined,
        consolidationGroupId: definition.hasConsolidation
            ? filters.consolidationGroupId
            : undefined,
        ...datesToViewFilters(filters),
    };

    return stripUndefined({ ...extras, ...context });
}

export function buildAccountingReportBuiltInViewFilters({
    reportId,
    filters,
    settings: { cycle, datePreset },
}: {
    reportId: AccountingReportViewId;
    filters: Pick<AccountingReportFilters, "consolidationGroupId">;
    settings: AccountingReportBuiltInViewSettings;
}): FiltersTypes {
    return {
        ...buildAccountingReportViewExtras({
            reportId,
            filters: { ...filters, cycle },
        }),
        datePreset,
    };
}

export function buildAccountingReportViewPeriodComparisonFilters({
    reportId,
    filters,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
}): FiltersTypes {
    return stripUndefined({
        ...buildAccountingReportViewExtras({ reportId, filters }),
        ...datesToViewFilters(filters),
    });
}

export function applyAccountingReportBuiltInView({
    reportId,
    viewId,
    pageContext,
    today = new Date(),
}: {
    reportId: AccountingReportViewId;
    viewId: AccountingReportBuiltInViewId;
    pageContext?: Pick<AccountingReportFilters, "consolidationGroupId">;
    today?: Date;
}): Partial<AccountingReportFilters> {
    const filters = { consolidationGroupId: pageContext?.consolidationGroupId };
    const definition = getAccountingReportViewDefinition(reportId, filters);
    const { cycle, datePreset } = getAccountingReportBuiltInViewSettings({
        viewId,
        defaultCycle: definition.defaultCycle,
    });
    return {
        ...pickExtras(definition, { cycle }),
        ...datesFromViewFilters({ datePreset }, today),
    };
}

export function applyAccountingReportViewFilters({
    reportId,
    viewFilters,
    today = new Date(),
}: {
    reportId: AccountingReportViewId;
    viewFilters: FiltersTypes;
    today?: Date;
}): Partial<AccountingReportFilters> {
    const definition = getAccountingReportViewDefinition(reportId, {
        consolidationGroupId: viewFilters.consolidationGroupId,
    });
    const extras = pickExtras(definition, viewFilters);

    const hasPageContext =
        viewFilters.entityId !== undefined ||
        viewFilters.consolidationGroupId !== undefined;
    if (!hasPageContext) {
        return extras;
    }

    return {
        ...extras,
        ...(definition.hasConsolidation ? CONSOLIDATED_CLEARED_EXTRAS : {}),
        entityId: viewFilters.entityId,
        ...(definition.hasGeneralLedgerFilters
            ? { entityIds: viewFilters.entityIds }
            : {}),
        ...(definition.hasLedger ? { ledgerId: viewFilters.ledgerId } : {}),
        ...(definition.canConsolidate
            ? { consolidationGroupId: viewFilters.consolidationGroupId }
            : {}),
        ...datesFromViewFilters(viewFilters, today),
    };
}

function buildExtras(
    definition: AccountingReportViewDefinition,
    filters: AccountingReportFilters,
): AccountingReportViewExtras {
    const extras = pickExtras(definition, filters);
    const hasSelectedClasses = (filters.classes?.length ?? 0) > 0;
    const isNoClasses = filters.classFilterType === ClassFilterType.NO_CLASSES;
    if (!hasSelectedClasses && !isNoClasses) {
        extras.classes = undefined;
        extras.classFilterType = undefined;
    }
    return extras;
}

function pickExtras(
    definition: AccountingReportViewDefinition,
    source: AccountingReportViewExtras,
): AccountingReportViewExtras {
    const extras: AccountingReportViewExtras = {
        classes: source.classes,
        classFilterType: source.classFilterType,
    };
    if (definition.defaultCycle !== undefined) {
        extras.cycle = source.cycle ?? definition.defaultCycle;
    }
    if (definition.comparisons.length > 0) {
        extras.comparisons = source.comparisons;
        extras.comparisonDiffs = source.comparisonDiffs;
    }
    if (definition.ratioTypes.length > 0) {
        extras.ratioTypes = source.ratioTypes;
    }
    if (definition.hasCounterparties) {
        extras.counterpartiesIds = source.counterpartiesIds;
        extras.withEmptyOrUnverifiedCounterparty =
            source.withEmptyOrUnverifiedCounterparty;
        extras.hasCounterparty = source.hasCounterparty;
    }
    if (definition.hasFinancialAccounts) {
        extras.accountIds = source.accountIds;
        extras.subaccountIds = source.subaccountIds;
    }
    if (definition.hasGeneralLedgerFilters) {
        extras.accountCodes = source.accountCodes;
        extras.journalEntrySources = source.journalEntrySources;
        extras.minAmount = source.minAmount;
        extras.maxAmount = source.maxAmount;
        extras.direction = source.direction;
        extras.amountRounded = source.amountRounded;
        extras.amountRoundingLevel = source.amountRoundingLevel;
        extras.excludeFrom1099 = source.excludeFrom1099;
        extras.search = source.search;
    }
    return extras;
}

// Hidden by AccountingReportFiltersForm under consolidation; stale values must not reach the backend.
const CONSOLIDATED_CLEARED_EXTRAS: Partial<AccountingReportFilters> = {
    cycle: undefined,
    comparisons: undefined,
    comparisonDiffs: undefined,
    ratioTypes: undefined,
};

function datesToViewFilters(filters: AccountingReportFilters): {
    start?: Date;
    end?: Date;
    datePreset?: string;
} {
    if (filters.isAllTime) {
        return { datePreset: ALL_TIME_PRESET_ID };
    }
    if (filters.datePreset) {
        return { datePreset: filters.datePreset };
    }
    return {
        start: filters.startDate
            ? localCalendarDayAsUTCMidnight(filters.startDate)
            : undefined,
        end: filters.endDate
            ? localCalendarDayAsUTCMidnight(filters.endDate)
            : undefined,
    };
}

function datesFromViewFilters(
    viewFilters: FiltersTypes,
    today: Date,
): Partial<
    Pick<
        AccountingReportFilters,
        "startDate" | "endDate" | "isAllTime" | "datePreset"
    >
> {
    if (viewFilters.datePreset === ALL_TIME_PRESET_ID) {
        return {
            isAllTime: true,
            datePreset: undefined,
            startDate: undefined,
            endDate: undefined,
        };
    }
    if (viewFilters.datePreset) {
        const preset = DATE_RANGE_PRESETS_BY_ID[viewFilters.datePreset];
        if (!preset) {
            return {};
        }
        return {
            datePreset: viewFilters.datePreset,
            isAllTime: undefined,
            startDate: preset.getStart(today),
            endDate: preset.getEnd(today),
        };
    }
    return {
        isAllTime: undefined,
        datePreset: undefined,
        startDate: viewFilters.start
            ? utcCalendarDayAsLocalMidnight(viewFilters.start)
            : undefined,
        endDate: viewFilters.end
            ? utcCalendarDayAsLocalMidnight(viewFilters.end)
            : undefined,
    };
}

function stripUndefined(filters: FiltersTypes): FiltersTypes {
    return Object.fromEntries(
        Object.entries(filters).filter(([, value]) => value !== undefined),
    );
}
